import React, { useState } from 'react';
import './Documentation.css';

const SECTIONS = [
  {
    id: 'demarrage',
    icon: '🚀',
    label: 'Démarrage rapide',
    keywords: 'debut commencer premiers pas connexion',
    content: (
      <>
        <p>
          Bienvenue dans MAP Gestion. Cette page rassemble l'essentiel pour démarrer.
          Pour toute question non couverte ici, consulter la section <strong>FAQ</strong> ou contacter
          le support.
        </p>

        <h3>Première connexion</h3>
        <ol>
          <li>Ouvrir <code>https://app.map-menuiserie.fr</code> dans un navigateur.</li>
          <li>Saisir son identifiant et son mot de passe.</li>
          <li>Le menu principal en haut de l'écran donne accès à tous les modules.</li>
        </ol>

        <h3>Workflow de base</h3>
        <ol>
          <li><strong>Créer un client</strong> dans <em>Clients</em>.</li>
          <li><strong>Créer une affaire</strong> liée à ce client dans <em>Affaires</em>.</li>
          <li>Sur la fiche affaire, <strong>générer un devis</strong>, l'envoyer au client.</li>
          <li>Une fois le devis accepté, <strong>convertir le devis en facture</strong>.</li>
          <li>Lorsque le client paie, <strong>enregistrer le paiement</strong> sur la facture.</li>
          <li>Le <em>Livre des recettes</em> agrège automatiquement tous les paiements.</li>
        </ol>

        <h3>Bonnes pratiques au quotidien</h3>
        <ul>
          <li>Toujours vérifier les coordonnées du client avant de générer un devis (le PDF reprend ces informations).</li>
          <li>Renseigner la date d'envoi du devis pour suivre les relances.</li>
          <li>Enregistrer les paiements dès leur encaissement, surtout pour les espèces (registre obligatoire).</li>
        </ul>
      </>
    ),
  },

  {
    id: 'clients',
    icon: '👥',
    label: 'Clients',
    keywords: 'client contact coordonnees particulier professionnel b2b b2c',
    content: (
      <>
        <p>
          Le module <em>Clients</em> centralise tous les contacts (particuliers et professionnels)
          avec qui une affaire peut être ouverte.
        </p>

        <h3>Créer un client</h3>
        <ol>
          <li>Menu <em>Clients</em> &rarr; bouton <strong>Nouveau client</strong>.</li>
          <li>Remplir les champs obligatoires : nom, téléphone et/ou email, adresse complète.</li>
          <li>Pour un client professionnel, renseigner aussi la raison sociale et le SIRET.</li>
          <li>Valider avec <strong>Enregistrer</strong>.</li>
        </ol>

        <h3>Modifier ou supprimer</h3>
        <ul>
          <li>Cliquer sur la ligne du client dans la liste pour ouvrir sa fiche.</li>
          <li>Le bouton <strong>Modifier</strong> permet de mettre à jour les informations.</li>
          <li>La suppression n'est possible que si aucune affaire n'est rattachée au client.</li>
        </ul>

        <h3>Voir les affaires d'un client</h3>
        <p>
          Sur la fiche client, la liste des affaires associées s'affiche directement,
          avec leur statut et leur montant.
        </p>
      </>
    ),
  },

  {
    id: 'affaires',
    icon: '📋',
    label: 'Affaires',
    keywords: 'affaire chantier projet dossier devis',
    content: (
      <>
        <p>
          Une <em>affaire</em> représente un chantier ou un projet rattaché à un client.
          C'est le point de départ de tout devis et de toute facture.
        </p>

        <h3>Créer une affaire</h3>
        <ol>
          <li>Menu <em>Affaires</em> &rarr; bouton <strong>Nouvelle affaire</strong>.</li>
          <li>Sélectionner le client concerné (créer le client au préalable s'il n'existe pas).</li>
          <li>Saisir un nom de projet clair (par exemple : <em>"Pose escalier - 12 rue X"</em>).</li>
          <li>Ajouter une description si nécessaire.</li>
          <li>Enregistrer.</li>
        </ol>

        <h3>Statuts d'une affaire</h3>
        <ul>
          <li><strong>En cours</strong> : affaire active, devis ou facture en circulation.</li>
          <li><strong>Terminée</strong> : tout est facturé et encaissé.</li>
          <li><strong>Annulée</strong> : le client n'a pas donné suite.</li>
        </ul>

        <h3>Actions disponibles</h3>
        <p>
          Depuis la fiche d'une affaire, il est possible de générer un devis,
          consulter les devis et factures déjà émis, et suivre l'état des paiements.
        </p>
      </>
    ),
  },

  {
    id: 'devis-factures',
    icon: '📄',
    label: 'Devis et factures',
    keywords: 'devis facture pdf email cgv notes b2b b2c assurance acompte solde',
    content: (
      <>
        <p>
          Les devis et factures sont générés depuis la fiche d'une affaire. Tous les documents
          sont produits au format PDF, prêts à être imprimés ou envoyés par email.
        </p>

        <h3>Créer un devis</h3>
        <ol>
          <li>Ouvrir la fiche de l'affaire concernée.</li>
          <li>Cliquer sur <strong>Nouveau devis</strong>.</li>
          <li>Ajouter les lignes : description, quantité, prix unitaire HT.</li>
          <li>Renseigner les frais éventuels (déplacement, fournitures) et la remise.</li>
          <li>Compléter le champ <em>Notes</em> avec les conditions particulières (voir ci-dessous).</li>
          <li>Enregistrer puis générer le PDF.</li>
        </ol>

        <h3>Notes : conditions particulières (B2B / B2C)</h3>
        <p>
          Le champ <em>Notes</em> du devis s'affiche dans un encart orange en bas du PDF,
          intitulé <strong>"Conditions particulières"</strong>. Y indiquer notamment :
        </p>
        <ul>
          <li>Les modalités de paiement (acompte, échéancier).</li>
          <li>La durée de validité du devis.</li>
          <li>Pour un client B2B : les mentions légales spécifiques (escompte, pénalités de retard).</li>
          <li>Pour un client B2C : la mention sur le droit de rétractation hors local commercial.</li>
        </ul>

        <h3>Mention assurance</h3>
        <p>
          Tous les devis et factures intègrent automatiquement la mention de l'assurance
          décennale en pied de document (assureur, numéro de contrat, périmètre).
          Ces informations sont configurées une seule fois dans <em>Administration</em>.
        </p>

        <h3>Convertir un devis en facture</h3>
        <ol>
          <li>Sur la fiche du devis accepté, cliquer sur <strong>Convertir en facture</strong>.</li>
          <li>La facture reprend toutes les lignes du devis.</li>
          <li>Possibilité de générer une <strong>facture d'acompte</strong> avant la facture de solde.</li>
          <li>La facture de solde déduit automatiquement les acomptes déjà encaissés sur ce devis.</li>
        </ol>

        <h3>Envoyer par email</h3>
        <ol>
          <li>Depuis le devis ou la facture, cliquer sur <strong>Envoyer par email</strong>.</li>
          <li>Le destinataire est pré-rempli à partir de la fiche client.</li>
          <li>Le PDF du document est joint automatiquement.</li>
          <li>Pour un devis B2C : joindre également le PDF des CGV.</li>
          <li>Modifier le sujet et le message si besoin, puis envoyer.</li>
        </ol>
      </>
    ),
  },

  {
    id: 'paiements',
    icon: '💶',
    label: 'Encaisser un paiement',
    keywords: 'paiement encaisser virement cheque especes plafond avoir remboursement',
    content: (
      <>
        <p>
          L'enregistrement d'un paiement se fait depuis la fiche d'une facture,
          via le bouton <strong>Encaisser</strong>.
        </p>

        <h3>Modes de paiement disponibles</h3>
        <table>
          <thead>
            <tr>
              <th>Mode</th>
              <th>Champs à renseigner</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Virement</strong></td>
              <td>Date d'encaissement, montant</td>
              <td>Date = date à laquelle la somme est créditée sur le compte.</td>
            </tr>
            <tr>
              <td><strong>Chèque</strong></td>
              <td>Date, montant, n° de chèque, banque émettrice, titulaire</td>
              <td>Tous les champs chèque sont obligatoires (traçabilité).</td>
            </tr>
            <tr>
              <td><strong>Espèces</strong></td>
              <td>Date, montant</td>
              <td>Plafond légal : <strong>1 000 €</strong> par opération (art. L112-6 CMF).</td>
            </tr>
          </tbody>
        </table>

        <h3>Plafond espèces</h3>
        <p>
          Au-delà de <strong>1 000 €</strong>, l'application affiche un avertissement
          et bloque la saisie. Ce plafond s'applique par opération de paiement, pas par client.
          Pour un encaissement supérieur, demander un virement ou un chèque.
        </p>

        <h3>Avoirs et remboursements</h3>
        <p>
          En cas d'annulation d'une commande déjà payée :
        </p>
        <ol>
          <li>Créer un <strong>avoir</strong> depuis la fiche de la facture concernée.</li>
          <li>Lorsque le remboursement est effectué (virement, chèque ou espèces),
            cliquer sur <strong>Rembourser</strong> en face de l'avoir.</li>
          <li>Renseigner le mode et la date du remboursement.</li>
          <li>L'avoir passe alors en statut <em>Remboursé</em>.</li>
        </ol>

        <h3>Corriger un paiement</h3>
        <p>
          Pour modifier ou supprimer un paiement enregistré par erreur, ouvrir la fiche
          de la facture, repérer la ligne dans la liste des paiements, et utiliser
          les boutons d'action en fin de ligne.
        </p>
      </>
    ),
  },

  {
    id: 'livre-recettes',
    icon: '📒',
    label: 'Livre des recettes',
    keywords: 'livre recettes export excel pdf especes registre micro entrepreneur fiscal',
    content: (
      <>
        <p>
          Le <em>Livre des recettes</em> est un registre <strong>obligatoire</strong> pour les
          micro-entrepreneurs. Il liste l'ensemble des encaissements de manière chronologique
          et inaltérable.
        </p>

        <h3>Accéder au livre</h3>
        <p>
          Menu <em>Livre des recettes</em>. La page affiche par défaut tous les paiements
          du mois en cours, classés par date.
        </p>

        <h3>Filtrer la période</h3>
        <ul>
          <li>Sélectionner un <strong>mois</strong> et/ou une <strong>année</strong> dans les filtres.</li>
          <li>Le bouton <strong>Rafraîchir</strong> recharge les données.</li>
          <li>Les totaux (HT, TTC, par mode de paiement) s'actualisent automatiquement.</li>
        </ul>

        <h3>Onglets disponibles</h3>
        <ul>
          <li><strong>Toutes</strong> : tous les paiements, tous modes confondus.</li>
          <li><strong>Espèces</strong> : uniquement les paiements en espèces, qui constituent
            le <em>registre des recettes en espèces</em> imposé par l'administration fiscale.</li>
        </ul>

        <h3>Exporter</h3>
        <ul>
          <li><strong>Excel</strong> : fichier <code>.xlsx</code> avec 3 feuilles
            (Livre complet, Registre espèces, Récapitulatif). À conserver dans la comptabilité.</li>
          <li><strong>PDF</strong> : version imprimable au format A4 paysage, à archiver ou présenter
            en cas de contrôle.</li>
        </ul>

        <h3>Conseil</h3>
        <p>
          Exporter le livre en PDF chaque fin de mois et archiver le fichier dans un dossier
          dédié (cloud ou disque dur). En cas de contrôle, les exports datés constituent une preuve fiable.
        </p>
      </>
    ),
  },

  {
    id: 'agenda',
    icon: '📅',
    label: 'Agenda',
    keywords: 'agenda calendrier rendez vous rdv planning',
    content: (
      <>
        <p>
          L'<em>Agenda</em> permet de noter les rendez-vous (visites, métrés, livraisons)
          en lien avec les affaires en cours.
        </p>

        <h3>Ajouter un rendez-vous</h3>
        <ol>
          <li>Menu <em>Agenda</em> &rarr; cliquer sur la date souhaitée.</li>
          <li>Saisir un titre clair (par exemple : <em>"Métré - M. Dupont"</em>).</li>
          <li>Renseigner l'heure de début et la durée.</li>
          <li>Optionnel : associer le rendez-vous à un client ou à une affaire.</li>
          <li>Enregistrer.</li>
        </ol>

        <h3>Modifier ou supprimer</h3>
        <ul>
          <li>Cliquer sur l'événement dans le calendrier.</li>
          <li>Modifier les champs ou utiliser le bouton <strong>Supprimer</strong>.</li>
        </ul>

        <h3>Vues disponibles</h3>
        <p>
          La vue <strong>Mois</strong> donne un aperçu général ; la vue <strong>Semaine</strong>
          détaille les créneaux horaires.
        </p>
      </>
    ),
  },

  {
    id: 'faq',
    icon: '❓',
    label: 'FAQ et dépannage',
    keywords: 'faq aide probleme erreur depannage support',
    content: (
      <>
        <h3>J'ai oublié mon mot de passe</h3>
        <p>
          Contacter l'administrateur (compte admin de l'application). Le mot de passe peut être
          réinitialisé depuis le module <em>Administration</em>.
        </p>

        <h3>L'envoi d'email a échoué</h3>
        <ul>
          <li>Vérifier que l'adresse email du client est correcte (pas de typo).</li>
          <li>Vérifier la connexion internet.</li>
          <li>Si le problème persiste, l'erreur est tracée dans les logs (voir avec l'administrateur).</li>
        </ul>

        <h3>Le PDF ne se génère pas</h3>
        <ul>
          <li>Rafraîchir la page et réessayer.</li>
          <li>Vérifier que tous les champs obligatoires du devis ou de la facture sont remplis.</li>
          <li>Si l'erreur persiste, signaler le numéro du document concerné au support.</li>
        </ul>

        <h3>Je ne peux pas supprimer un client</h3>
        <p>
          La suppression est bloquée si le client a au moins une affaire. Supprimer ou archiver
          d'abord les affaires associées, puis recommencer.
        </p>

        <h3>Un paiement a été enregistré sur la mauvaise facture</h3>
        <ol>
          <li>Ouvrir la fiche de la facture concernée.</li>
          <li>Supprimer le paiement erroné.</li>
          <li>Le re-saisir sur la bonne facture.</li>
        </ol>

        <h3>Le total du livre des recettes ne correspond pas à mon relevé bancaire</h3>
        <ul>
          <li>Le livre liste les paiements <strong>encaissés</strong> (date d'encaissement),
            pas la date de facturation. Vérifier que les dates correspondent au mois sélectionné.</li>
          <li>Les paiements en espèces ne figurent pas sur le relevé bancaire — comparer plutôt
            le total <em>virements + chèques</em> du livre avec les crédits bancaires.</li>
        </ul>

        <h3>Besoin d'aide supplémentaire</h3>
        <p>
          Contact support : <code>commercial@map-menuiserie.fr</code>
        </p>
      </>
    ),
  },
];

function Documentation() {
  const [activeId, setActiveId] = useState('demarrage');
  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  const filteredSections = query
    ? SECTIONS.filter(s =>
        (s.label + ' ' + s.keywords).toLowerCase().includes(query)
      )
    : SECTIONS;

  const activeSection =
    SECTIONS.find(s => s.id === activeId) || SECTIONS[0];

  return (
    <div className="documentation-container">
      <header className="doc-header">
        <h1>📚 Documentation MAP Gestion</h1>
        <input
          className="doc-search"
          type="search"
          placeholder="Rechercher une section..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </header>

      <div className="doc-layout">
        <aside className="doc-sidebar">
          <nav className="doc-nav">
            {filteredSections.length === 0 ? (
              <div className="no-results">Aucune section trouvée</div>
            ) : (
              filteredSections.map(s => (
                <button
                  key={s.id}
                  className={`nav-item ${s.id === activeId ? 'active' : ''}`}
                  onClick={() => setActiveId(s.id)}
                >
                  <span className="nav-icon">{s.icon}</span>
                  {s.label}
                </button>
              ))
            )}
          </nav>
        </aside>

        <main className="doc-content">
          <h2>
            {activeSection.icon} {activeSection.label}
          </h2>
          <div className="doc-body">{activeSection.content}</div>
        </main>
      </div>

      <footer className="doc-footer">
        MAP Gestion — Documentation utilisateur · Pour toute question :{' '}
        <code>commercial@map-menuiserie.fr</code>
      </footer>
    </div>
  );
}

export default Documentation;
